import React, { useEffect, useRef, useState } from 'react';

import { DataPipeline } from '@finley-technologies/finley-types';

import { useAllRecentMeshRuns } from '../../hooks/useRecentMeshRuns';
import { UploadDrawerHeader } from './DrawerHeader';
import { NeededInput } from './neededInput';
import { SelectExistingMeshRun } from './SelectExistingMeshRun';
import { UploadDrawerSkeletonContent } from './UploadDrawerShell';
import {
  UploadForExistingPipeline,
  UploadForNewPipeline,
} from './UploadForPipeline';
import { NeededInputProvider } from 'packages/dataPipeline/hooks/neededInputContext';
import { SlugToNameProvider } from 'packages/dataPipeline/hooks/slugToNameContext';
import {
  MeshRunParams,
  SelectedMeshRun,
} from '~/packages/dataPipeline/types/dataPipelineTypes';

type DrawerState =
  | {
      // drawer is showing the select pipeline tab
      state: 'selectMeshRun';
    }
  | {
      // drawer is showing upload tab for new mesh run
      state: 'uploadNew';
    }
  | {
      // drawer is showing upload tab for existing mesh run
      state: 'uploadExisting';
      selectedMesh: SelectedMeshRun;
    };

// a run the drawer is opened for: an existing run, or (meshRunId null) the
// new run of the given version
export type LockedMeshRun = {
  meshRunId: DataPipeline.MeshRunId | null;
  meshRunParams: MeshRunParams;
};

const useDrawerState = (
  lockedMeshRun: LockedMeshRun | null,
): [DrawerState, React.Dispatch<React.SetStateAction<DrawerState>>] =>
  useState<DrawerState>(
    (): DrawerState => {
      if (!lockedMeshRun) {
        return { state: 'selectMeshRun' };
      }
      const { meshRunId, meshRunParams } = lockedMeshRun;
      return meshRunId
        ? {
            state: 'uploadExisting',
            selectedMesh: { meshRunId, meshRunParams },
          }
        : { state: 'uploadNew' };
    },
  );

/* pipeline drawer lets you choose an existing pipeline or start a new one */
const UploadDrawerContentInternal = ({
  organizationId,
  meshDocuments,
  lockedMeshRun,
  allRecentMeshRuns,
  onClose,
  refetchMeshRuns,
}: {
  organizationId: string;
  meshDocuments: DataPipeline.MeshDocumentMetadata[];
  lockedMeshRun: LockedMeshRun | null;
  allRecentMeshRuns: DataPipeline.MeshRunRecord[];
  onClose: () => void;
  refetchMeshRuns: () => void;
}): React.ReactElement => {
  const [childKey, setChildKey] = useState(0);
  const [drawerState, setDrawerState] = useDrawerState(lockedMeshRun);

  // are we asked to start with a specific (new or existing) upload?
  const showNewReportButton = !lockedMeshRun;
  const showLatestPipelineButton = !lockedMeshRun;

  // we want to show back button when we are showing upload tab for existing mesh run
  // however, we don't want to show back button when UploadDrawer is opened for a specific pipeline
  // from pipeline. (pipeline details page or verification flow )
  const showBackButton =
    drawerState.state === 'uploadExisting' && !lockedMeshRun;

  const onShowLatestPipelines = () => {
    refetchMeshRuns();
    setDrawerState({ state: 'selectMeshRun' });
  };

  const onNewReport = () => {
    // updating child key to force re-render of the new upload tab
    // this resets the state (date/mesh selection) of the new upload tab
    setChildKey((prevKey) => prevKey + 1);
    setDrawerState({ state: 'uploadNew' });
  };

  const hasInitializedCorrectTab = useRef(false);

  // switch to new upload tab if there are no recent mesh runs
  const numRecentRuns = allRecentMeshRuns.length;
  useEffect(() => {
    if (drawerState.state === 'selectMeshRun' && numRecentRuns === 0) {
      if (hasInitializedCorrectTab.current) {
        // we run this hook only once
        // to switch to new tab if there are no recent mesh runs.
        // if user chooses to go back to to select mesh run tab, we don't want to switch back
        // we will fetch the latest runs when user goes back to select mesh run tab
        return;
      }

      setDrawerState({ state: 'uploadNew' });
      hasInitializedCorrectTab.current = true;
    }
  }, [drawerState.state, numRecentRuns, setDrawerState]);

  return (
    <>
      <UploadDrawerHeader
        showBackButton={showBackButton}
        onBack={onShowLatestPipelines}
        onClose={onClose}
        onNewReport={showNewReportButton ? onNewReport : undefined}
        onShowLatestPipelines={
          showLatestPipelineButton ? onShowLatestPipelines : undefined
        }
      />
      {drawerState.state === 'selectMeshRun' && (
        <SelectExistingMeshRun
          allRecentMeshRuns={allRecentMeshRuns}
          latestMeshDocuments={meshDocuments}
          onClose={onClose}
          onPipelineSelected={(selectedMesh) => {
            setDrawerState({
              state: 'uploadExisting',
              selectedMesh,
            });
          }}
        />
      )}
      {drawerState.state === 'uploadNew' && (
        <UploadForNewPipeline
          key={childKey}
          organizationId={organizationId}
          rdfv={lockedMeshRun?.meshRunParams.rdfv ?? null}
          latestMeshDocuments={meshDocuments}
        />
      )}
      {drawerState.state === 'uploadExisting' && (
        <UploadForExistingPipeline
          organizationId={organizationId}
          selectedMeshRun={drawerState.selectedMesh}
        />
      )}
    </>
  );
};

/**
 *  This control fetches the drawer's recent runs and passes them on to
 *  UploadDrawerContentInternal for rendering.
 *  The caller is responsible for putting it inside the shell.
 *  We do this to enable caller to render skeleton insider the drawer
 *  while the data is loading
 */
export const UploadDrawerContent = ({
  organizationId,
  onClose,
  meshDocuments,
  lockedMeshRun,
  neededInput,
}: {
  organizationId: string;
  onClose: () => void;
  // the meshes the drawer offers
  meshDocuments: DataPipeline.MeshDocumentMetadata[];
  // when given, the drawer shows only this run
  lockedMeshRun: LockedMeshRun | null;
  // when given, the drawer marks the inputs that match it
  neededInput: NeededInput | null;
}): React.ReactElement => {
  const {
    recentMeshRuns,
    loading: loadingRecentMeshRuns,
    refetch: refetchMeshRuns,
  } = useAllRecentMeshRuns({
    organizationId,
    // a locked drawer never lists the recent runs
    skip: !!lockedMeshRun,
  });

  if (loadingRecentMeshRuns) {
    return <UploadDrawerSkeletonContent onClose={onClose} />;
  }

  // after fetching the data, we render the actual content
  return (
    <SlugToNameProvider
      organizationId={organizationId}
      loadingComponent={<UploadDrawerSkeletonContent onClose={onClose} />}
    >
      <NeededInputProvider neededInput={neededInput}>
        <UploadDrawerContentInternal
          organizationId={organizationId}
          onClose={onClose}
          meshDocuments={meshDocuments}
          lockedMeshRun={lockedMeshRun}
          allRecentMeshRuns={recentMeshRuns}
          refetchMeshRuns={refetchMeshRuns}
        />
      </NeededInputProvider>
    </SlugToNameProvider>
  );
};
